/**
 * El panel: el estado del libro antes que la caja de texto.
 *
 * La diferencia con un chat con menús es el orden. Aquí lo primero que lee el autor es en qué
 * punto está su manuscrito; el chat es un módulo al pie, no la puerta de entrada.
 *
 * La lateral MANDA: cada entrada lleva a una sección de verdad. Mientras fue decoración —cifras
 * inventadas, clics que no iban a ninguna parte— la aplicación aparentaba más de lo que hacía,
 * que es exactamente la clase de promesa que nos ha costado dos días de soporte esta semana.
 */
import BordeChat, { useAnchoChat } from "./piezas/BordeChat";
import { useEffect, useRef, useState } from "react";
import { useAuth, useClerk, useUser } from "@clerk/react";
import Lateral from "./piezas/Lateral";
import Barra, { HABLAR_EN_ETAPA, HIJAS, PADRE, PADRE_FLUJO, TODAS } from "./piezas/Barra";
import { Flujo } from "./flujo";
import Control from "./piezas/Control";
import Etapa, { PrimeroElManuscrito } from "./piezas/Etapa";
import { useCamino } from "./piezas/useCamino";
import SubirDocumento from "./piezas/SubirDocumento";
import { useTrabajos } from "./piezas/useTrabajos";
import Terminados from "./piezas/Terminados";
import Fase from "./piezas/Fase";
import GastoGrafico from "./piezas/Gasto";
import Gastos from "./piezas/Gastos";
import { useChat, Conversacion, Compositor } from "./piezas/Chat";
import Proyecto from "./piezas/Proyecto";
import Manuscrito from "./piezas/Manuscrito";
import Informe from "./piezas/Informe";
import Ajustes from "./piezas/Ajustes";
import Ayuda from "./piezas/Ayuda";
import Reescritura from "./piezas/Reescritura";
import Pendiente from "./piezas/Pendiente";
import Bitacora from "./piezas/Bitacora";
import Certificado from "./piezas/Certificado";
import Procedencia from "./piezas/Procedencia";
import Aplicar from "./piezas/Aplicar";
import Pasadas from "./piezas/Pasadas";
import Puerta, { type Paso } from "./piezas/Puerta";
import Entrada from "./piezas/Entrada";
import Modelos from "./piezas/Modelos";
import { estadoManuscrito, traerAlProyecto, clave as comprobarClave } from "./api";
import type { EstadoManuscrito } from "./api";
import SinDecidir from "./piezas/SinDecidir";
import Proyectos from "./piezas/Proyectos";
import Autores from "./piezas/Autores";
import Estilos from "./piezas/Estilos";
import Salvavidas from "./piezas/Salvavidas";
import Suscribir from "./piezas/Suscribir";
import { colaboradores, telemetria, salud, proyecto, proyectos, guardarDocumentoAbierto, documentoPendiente, estadoSesion, salir, entrarConCuenta, FalloCuenta, bandeja, bitacoraResumen, dolares, miles,
         suscripcion, ultimaVersion, hayNueva, nombreDe, saludoDelDia, documento, descargas, sistema, mostrarEnCarpetaCon } from "./api";
import type { Colaborador, Telemetria, Salud, Fichero, Asomo, Resumen } from "./api";
import { ETAPAS, flujoActivo, conQuienEnElFlujo, avisoDeIndicaciones, avisoDeCarpeta, avisoDeClave, carpetaImposible, dentroDeCarpeta, nombreDeRuta, puntoDelLibro, queEnsenar, ultimaVersionDe, versionDeTuSistema } from "./logica";
import type { IdEtapa, PuntoDelLibro } from "./logica";

const TITULOS: Record<string, string> = {
  ...Object.fromEntries(TODAS.map((i) => [i.id, i.et])),
  panel: "Proyectos", bitacora: "Bitácora", pasadas: "Pasadas", comparar: "Comparar versiones", control: "Control",
  "informe-lectura": "Informe de lectura", ajustes: "Ajustes",
  biblia: "Biblia", escaleta: "Escaleta",
};

/** Lo que cada sección hará y lo que le falta. Escrito aquí para no repetirlo en diez ficheros. */
const PORHACER: Record<string, [string, string]> = {
  biblia: ["La biblia del libro: personajes, mundo y reglas, viva y consultable.", "leer el documento de la carpeta y mostrarlo aquí; hoy se pide al coach desde el chat."],
  escaleta: ["La escaleta capítulo a capítulo, enlazada con el manuscrito.", "lo mismo que la biblia: la sección la lee, no la inventa."],
};

/** Una línea por sección construida: lo que el autor encuentra si la abre. Las que faltan ya lo
 *  cuentan en PORHACER, y la pantalla de fase usa una u otra sin distinguir. */
const QUEHACE: Record<string, string> = {
  panel: "El estado de tu libro: lo que llevas gastado, lo que ha trabajado el equipo y tus documentos.",
  manuscrito: "Tus documentos. El que abras aquí es sobre el que trabaja todo el equipo.",
  "informe-lectura": "Un informe de lectura capítulo a capítulo, con cada problema localizado donde está.",
  estilos: "Los diez ejes de tu voz, con quién se parece y qué se deja sin puntuar a propósito.",
  bitacora: "Qué pediste, qué te respondieron, qué costó y qué decidiste. Es lo que sostiene el certificado.",
  registro: "La bitácora mirada desde la autoría: qué párrafos son tuyos y cuáles vienen de la máquina.",
  pasadas: "Cómo ha crecido o encogido tu libro versión a versión, y qué parte de lo propuesto te quedaste.",
  comparar: "Dos versiones enfrentadas: qué cambió entre una y otra, y cuánto.",
};

export default function App() {
  // LA CUENTA, lo primero. Sin sesión no se pinta nada del producto —como en cualquier panel de
  // verdad, `dashboard.` redirige a entrar—. El hook va arriba del todo: las salidas tempranas de
  // más abajo no pueden cambiar el orden de los hooks.
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const clerk = useClerk();
  // El nombre del saludo sale de la CUENTA (R-08); el de la licencia de Word queda de plan B.
  const { user } = useUser();
  const quienSaluda = user?.firstName || nombreDe(user?.primaryEmailAddress?.emailAddress) || null;
  // Solo en desarrollo: `?romper` tira un fallo de pintado para ver el salvavidas sin esperar a uno.
  if (import.meta.env.DEV && new URLSearchParams(location.search).has("romper")) throw new Error("prueba del salvavidas (?romper)");
  const [equipo, setEquipo] = useState<Colaborador[]>([]);
  const [tele, setTele] = useState<Telemetria | null>(null);
  const [est, setEst] = useState<Salud | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [seccion, setSeccion] = useState("proyectos");
  // EL STUDIO COMO FLUJO (3-oct-2026; src/flujo.ts): `?flujo=1` lo enciende en este navegador.
  const [flujo] = useState(() => {
    const guardado = (() => { try { return localStorage.getItem("hi.flujo"); } catch { return null; } })();
    const f = flujoActivo(location.search, guardado);
    try { if (new URLSearchParams(location.search).has("flujo")) localStorage.setItem("hi.flujo", f ? "1" : "0"); } catch { /* sin almacenamiento, vale para esta visita */ }
    return f;
  });
  // «Ver cuáles» abre la Bitácora filtrada en lo que falta por decidir (R-02). Ir a cualquier otra
  // sección lo suelta.
  const [soloPendientes, setSoloPendientes] = useState(false);
  // Con quién estás hablando. Es una navegación distinta de la sección: la izquierda es gente y
  // la de arriba es trabajo, y mezclarlas en un solo estado obligaría a inventar una jerarquía
  // entre «el corrector» y «la bitácora» que no existe.
  const [hablando, setHablando] = useState<string | null>(null);
  const anchoChat = useAnchoChat();
  const [bandejaConv, setBandejaConv] = useState<Asomo[]>([]);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  // `null` mientras no se sabe: pintar el panel y quitarlo después sería enseñar el manuscrito
  // durante medio segundo a quien no ha entrado.
  const [puerta, setPuerta] = useState<Paso | { modo: "dentro"; conPassword: boolean } | null>(null);
  const renovar = useRef<number | undefined>(undefined);

  // Quién es y si su motor se ha quedado atrás. Las dos son baratas y se piden una vez.
  const [nombre, setNombre] = useState<string | null>(null);
  const [nueva, setNueva] = useState<{ ultima: string; url: string | null } | null>(null);
  useEffect(() => {
    suscripcion().then((s) => setNombre(nombreDe(s.licencia?.correo))).catch(() => {});
    // La última versión es LA DE SU SISTEMA (2-oct): Mac y Windows pueden ir a distinta, y el aviso
    // comparaba siempre con la de Mac. El manifiesto del propio Studio dice las dos; el motor sigue
    // diciendo cuál tiene instalada. Si el manifiesto no llega, vale lo que diga el motor.
    Promise.all([ultimaVersion(), descargas()])
      .then(([v, m]) => {
        const suya = m ? versionDeTuSistema(m, sistema()) : { version: v.ultima, url: v.url };
        if (hayNueva(v.version, suya.version)) setNueva({ ultima: suya.version!, url: suya.url ?? v.url });
      })
      .catch(() => {});   // sin red no se avisa de nada, y no se rompe
  }, []);

  // LA PUERTA DEL MOTOR (Cuentas III). Entrar con tu cuenta abre también tu HumanInk: el token de
  // Clerk va al motor, el motor se lo enseña al worker y abre la sesión local. Nada que teclear.
  // Un motor de antes de las cuentas no dice `cuentas` y sigue con su contraseña de siempre.
  const abrirPuerta = async (password?: string) => {
    let e;
    try { e = await estadoSesion(); }
    catch { setPuerta({ modo: "dentro", conPassword: false }); return; }   // sin motor: el panel ya dice qué hacer
    if (!e.cuentas) {
      setPuerta(e.cerrado && !e.dentro ? { modo: "password", cerrado: e.cerrado } : { modo: "dentro", conPassword: e.cerrado });
      return;
    }
    try {
      const token = await getToken();
      if (!token) throw new FalloCuenta("Tu sesión ha caducado. Vuelve a entrar.", "sin_token");
      const r = await entrarConCuenta(token, password);
      setPuerta({ modo: "dentro", conPassword: false });
      // El testigo del motor dura doce horas: se renueva antes, sin que el autor lo note.
      window.clearTimeout(renovar.current);
      renovar.current = window.setTimeout(() => { abrirPuerta(); }, Math.max(60, r.caduca - 600) * 1000);
    } catch (err) {
      if (!(err instanceof FalloCuenta)) { setPuerta({ modo: "dentro", conPassword: false }); return; }
      if (err.codigo === "migrar_password") {
        setPuerta({ modo: "migrar", mensaje: err.message, error: password ? "Esa no es la contraseña de este ordenador." : undefined });
      } else {
        setPuerta({ modo: "aviso", mensaje: err.message, codigo: err.codigo });
      }
    }
  };
  useEffect(() => { if (isSignedIn) abrirPuerta(); }, [isSignedIn]);

  useEffect(() => () => window.clearTimeout(renovar.current), []);

  const refrescarGasto = () => { telemetria().then(setTele).catch(() => {}); };

  useEffect(() => {
    Promise.all([colaboradores(), telemetria(), salud()])
      .then(([c, t, s]) => { setEquipo(c); setTele(t); setEst(s); })
      // El motor corre en el equipo del autor: si no está, se dice qué hacer, no «error de red».
      .catch(() => setFallo(
        "HumanInk no está funcionando en este ordenador. Si acabas de instalarlo, espera unos " +
        "segundos y recarga esta página; si no, vuelve a instalarlo desde humanink.io/studio.",
      ));
  }, []);

  const nombres = Object.fromEntries(equipo.map((c) => [c.id, c.name]));
  const gasto = tele?.total.cost ?? 0;
  // El proyecto activo sobrevive al cierre: se pregunta una vez, no en cada arranque.
  const [dir, setDir] = useState(() => localStorage.getItem("hi.proyecto") ?? "");
  const [ficheros, setFicheros] = useState<Fichero[]>([]);
  const [doc, setDoc] = useState<string | null>(() => localStorage.getItem("hi.documento"));
  // LA CARPETA GUARDADA NO PUEDE SER UN PROYECTO (Rais, 23-sep: el Studio abría «Downloads» y su
  // proyecto de verdad estaba a un clic). Si el motor tiene un proyecto activo, se abre ese: es lo
  // que el autor estaba usando, y el navegador solo guardaba un resto de otra sesión.
  useEffect(() => {
    if (!carpetaImposible(dir, ficheros.length)) return;
    let vigente = true;
    proyectos()
      .then((r) => {
        const suyo = r.proyectos.find((p) => p.id === r.activo && p.carpeta && !carpetaImposible(p.carpeta));
        if (!vigente || !suyo) return;
        localStorage.setItem("hi.proyecto", suyo.carpeta);
        setDir(suyo.carpeta);
        setAvisoArriba(`Has vuelto a tu proyecto «${suyo.nombre}». Lo que había abierto era una carpeta del sistema, no un libro.`);
      })
      .catch(() => {});
    return () => { vigente = false; };
  }, [dir, ficheros.length]);

  // La carpeta guardada puede haber desaparecido entre sesiones: movida, renombrada o en un disco
  // que hoy no está enchufado. Callarlo deja el panel con todo a cero y sin explicación.
  const [perdido, setPerdido] = useState<string | null>(null);
  // MANUSCRITO I: el motor busca biblia/escaleta por su nombre exacto (antes se adivinaba aquí
  // mismo con un `startsWith` que "bibliagrafia.docx" también hacía saltar) y dice a qué
  // colaborador conviene pedir lo que falta. `null` = todavía no se sabe; un motor anterior a
  // esta ruta se lee como "sin nada", nunca como un error que tape la pantalla.

  // EL PULSO. Las pantallas se leían una vez al entrar y no se volvían a leer nunca: encargabas un
  // informe desde el chat y Analizar seguía diciendo que no lo habías pedido; pulsabas «me lo
  // quedo» y Autoría seguía diciendo «aún no has decidido nada». Sube cada vez que pasa algo, y
  // todo lo que lee del motor cuelga de él.
  const [pulso, setPulso] = useState(0);
  const latir = () => setPulso((n) => n + 1);
  // EL MOTOR SE REINICIA Y EL TESTIGO CADUCA (Rais, 23-sep: tras actualizar a la 1.6.11, el plan,
  // el informe y los trabajos daban 401 y la pantalla los pintaba como «sin datos»). El testigo se
  // renovaba a las doce horas; ahora, en cuanto el motor dice que la sesión está cerrada, se vuelve
  // a abrir sola con la cuenta que ya está dentro.
  useEffect(() => {
    if (!isSignedIn) return;
    let vigente = true;
    estadoSesion()
      .then((e) => { if (vigente && e.cuentas && e.cerrado && !e.dentro) abrirPuerta(); })
      .catch(() => {});
    return () => { vigente = false; };
  }, [isSignedIn, pulso]);
  const [estadoMs, setEstadoMs] = useState<EstadoManuscrito | null>(null);
  useEffect(() => {
    if (!dir || seccion !== "manuscrito") return;
    estadoManuscrito(dir).then(setEstadoMs).catch(() => setEstadoMs({ disponible: false }));
  }, [dir, seccion, pulso]);
  const [abierto, setAbierto] = useState<{ ruta: string; texto: string } | null>(null);
  // EL MANUSCRITO ELEGIDO ES EL DOCUMENTO DE TRABAJO (César, 17-sep). Hasta hoy el corrector solo
  // tenía texto si el autor abría el documento a mano en «Manuscrito»; si no, corregía su mensaje.
  // Ahora el documento señalado se abre solo, y si en la carpeta hay un único documento, es ese.
  useEffect(() => {
    if (doc || !dir) return;
    const docs = ficheros.filter((f) => /\.(docx|md|txt)$/i.test(f.name) && !/^(informe|plan-reescritura|perfil-autor)/i.test(f.name));
    if (docs.length === 1) { localStorage.setItem("hi.documento", docs[0].path); setDoc(docs[0].path); }
  }, [dir, doc, ficheros]);
  useEffect(() => {
    if (!doc || abierto?.ruta === doc) return;
    documento(doc).then((d) => { if (!("error" in d)) setAbierto({ ruta: doc, texto: d.text }); }).catch(() => {});
  }, [doc]);

  // Si ya había manuscrito elegido, se recuperan sus documentos al arrancar.
  useEffect(() => {
    if (!dir) return;
    proyecto(dir)
      .then((r) => {
        if ("error" in r) return;
        setPerdido(r.existe === false ? dir : null);
        setFicheros(r.files);
      })
      .catch(() => {});
    // También con el pulso: una pasada crea una versión nueva y la lista tiene que enterarse, o el
    // manuscrito recién nacido parece que no existe (Rais, 23-sep: «No encuentro tu manuscrito»
    // arriba y «tu manuscrito ahora es esa versión» justo debajo).
  }, [dir, pulso]);

  // La bandeja se relee cuando cambia el proyecto y después de cada turno: las horas de la barra
  // tienen que ser las de verdad, no las del momento en que se abrió la aplicación.
  const refrescarBandeja = () => {
    if (!dir) { setBandejaConv([]); return; }
    bandeja(dir).then(setBandejaConv).catch(() => {});
  };
  useEffect(refrescarBandeja, [dir, pulso]);

  // Lo que el autor tiene a medias. Se lee de la bitácora del libro, así que es un hecho
  // registrado y no una cuenta que llevemos en memoria.
  useEffect(() => {
    if (!dir) { setResumen(null); return; }
    bitacoraResumen(dir, doc).then(setResumen).catch(() => setResumen(null));
  }, [dir, doc, tele, pulso]);
  // Lo que espera en Word se lee solo cuando guardas y cierras el documento: mientras quede algo,
  // se vuelve a mirar cada poco para que la cifra baje sin recargar nada.
  const enWord = resumen?.en_word?.sin_leer ?? 0;
  useEffect(() => {
    if (!dir || !enWord) return;
    const t = setTimeout(() => bitacoraResumen(dir, doc).then(setResumen).catch(() => {}), 10000);
    return () => clearTimeout(t);
  }, [dir, doc, enWord, resumen]);

  const chat = useChat(equipo, refrescarGasto, dir, abierto, refrescarBandeja, latir);

  /** Abrir a alguien: la conversación ocupa el lienzo y la sección de trabajo espera donde estaba. */
  // En el Studio nuevo solo se habla con el coach: lo pida quien lo pida, se abre él.
  const hablar = (id: string) => { const quien = flujo ? conQuienEnElFlujo(id) : id; setHablando(quien); chat.setElegido(quien); };
  /** Ir a una sección NO cierra la conversación (Fase 12, R-12): el chat vive a la derecha. */
  // VOLVER ATRÁS (Rais, 21-sep: «entras en el informe de lectura, ves los gráficos, y no puedes
  // volver»). Cada salto guarda de dónde venías; la flecha de arriba te devuelve ahí.
  const [historial, setHistorial] = useState<string[]>([]);
  const irA = (id: string) => {
    if (id !== seccion) setHistorial((h) => [...h.slice(-19), seccion]);
    setSeccion(id); setSoloPendientes(false);
  };
  const volver = () => {
    setHistorial((h) => {
      const anterior = h.at(-1);
      if (anterior) { setSeccion(anterior); setSoloPendientes(false); }
      return h.slice(0, -1);
    });
  };
  // El aviso que sobrevive al cambio de pantalla: al subir el manuscrito la pantalla de «empieza
  // por tu manuscrito» desaparece, y con ella lo que se le decía al autor.
  const [avisoArriba, setAvisoArriba] = useState<string | null>(null);
  // La llave de OpenRouter, comprobada al abrir y al salir de Ajustes (UAT 28-sep, H1): que no valga
  // se sabe antes del primer encargo, no por él. Comprobarla no gasta.
  const [avisoLlave, setAvisoLlave] = useState<string | null>(null);
  useEffect(() => {
    if (seccion === "ajustes") return;
    let vivo = true;
    comprobarClave().then((c) => { if (vivo) setAvisoLlave(avisoDeClave(c)); }).catch(() => {});
    return () => { vivo = false; };
  }, [seccion === "ajustes"]);
  // El manuscrito señalado ya no está entre los ficheros de la carpeta (renombrado o movido).
  /** Abrir el chat con un agente y un encargo mínimo ya escrito en la caja. */
  const hablarCon = (agente: string, encargo: string) => {
    hablar(agente);
    if (flujo || agente !== "reescritura") chat.setEncargo(encargo);
  };
  const esEtapa = (id: string): id is IdEtapa => ETAPAS.some((e) => e.id === id);
  // EL CAMINO: el estado de las cinco etapas, deducido de lo que ya guarda el motor (R-09).
  const camino = useCamino({ dir, doc, ficheros, resumen, pulso });
  // LOS TRABAJOS EN SEGUNDO PLANO (R-17): informes y mediciones que siguen aunque se cierre la pestaña.
  const trab = useTrabajos(dir, pulso, latir);
  // En qué etapa está trabajando el motor: se ve en la barra de arriba, sin entrar (Rais, 23-sep:
  // «cuando está trabajando se tiene que ver»).
  const ETAPA_DEL_TRABAJO: Record<string, string> = {
    "informe-global": "diagnostico", "informe-editor": "diagnostico", "informe-beta": "diagnostico",
    "informe-estilo": "diagnostico", "informe-comparado": "diagnostico",
    plan: "plan", pasada: "pasadas", operaciones: "pasadas", medir: "pasadas", correccion: "correccion",
    // M-5 y M-3: la fase entera es de Pasadas; dividir el libro en escenas, del Plan.
    fase: "pasadas", escenas: "plan",
  };
  const etapaTrabajando = trab.lista.find((t) => t.estado === "en_curso" && ETAPA_DEL_TRABAJO[t.tipo])
    ? ETAPA_DEL_TRABAJO[trab.lista.find((t) => t.estado === "en_curso" && ETAPA_DEL_TRABAJO[t.tipo])!.tipo]
    : null;
  // Y no se dice «no encuentro tu manuscrito» mientras hay una pasada en marcha: el documento que
  // falta es justo el que se está escribiendo.
  const docDesaparecido = !!doc && !perdido && ficheros.length > 0 && !ficheros.some((f) => f.path === doc)
    && !trab.lista.some((t) => t.estado === "en_curso" && ["pasada", "operaciones", "correccion", "fase"].includes(t.tipo));
  const subido = (f: Fichero) => setFicheros((v) => (v.some((x) => x.path === f.path) ? v : [...v, f]));
  const señalar = (ruta: string) => {
    localStorage.setItem("hi.documento", ruta); setDoc(ruta);
    // Y en el MOTOR (23-sep): lo que tienes abierto vive en tu ordenador, no en esta pestaña, y así
    // una recarga, otro navegador o un corte de luz te devuelven al mismo sitio.
    proyectos().then((r) => {
      const suyo = r.proyectos.find((p) => p.carpeta && ruta.startsWith(p.carpeta));
      if (suyo) guardarDocumentoAbierto(suyo.id, ruta);
    }).catch(() => {});
  };

  // ¿TU MANUSCRITO ESTÁ DECIDIDO Y GUARDADO? (Rais, 23-sep). El Studio solo ve el disco: si
  // aceptaste en Word y no guardaste, para él sigue sin decidir. Se pregunta al volver a esta
  // ventana —que es cuando vienes de Word— y se dice arriba, antes de que una pasada lo descubra.
  const [pendiente, setPendiente] = useState<{ marcas: number | null; abierto_en_word: boolean; indicaciones?: number } | null>(null);
  useEffect(() => {
    if (!doc) { setPendiente(null); return; }
    let vigente = true;
    const mirar = () => { documentoPendiente(doc).then((r) => { if (vigente) setPendiente(r); }).catch(() => {}); };
    mirar();
    const alVolver = () => { if (document.visibilityState === "visible") mirar(); };
    window.addEventListener("focus", mirar);
    document.addEventListener("visibilitychange", alVolver);
    return () => { vigente = false; window.removeEventListener("focus", mirar); document.removeEventListener("visibilitychange", alVolver); };
  }, [doc, pulso]);

  // AL CARGAR, LO QUE DICE EL MOTOR MANDA  // AL CARGAR, LO QUE DICE EL MOTOR MANDA sobre lo que recuerde la pestaña: la pestaña puede ser
  // otra, de otro dominio, o de antes de un reinicio.
  useEffect(() => {
    let vigente = true;
    proyectos().then((r) => {
      const p = r.proyectos.find((x) => x.id === r.activo);
      if (!vigente || !p?.carpeta || carpetaImposible(p.carpeta)) return;
      if (p.carpeta !== localStorage.getItem("hi.proyecto")) { localStorage.setItem("hi.proyecto", p.carpeta); setDir(p.carpeta); }
      if (p.documento && p.documento !== localStorage.getItem("hi.documento")) { localStorage.setItem("hi.documento", p.documento); setDoc(p.documento); }
    }).catch(() => {});
    return () => { vigente = false; };
  }, []);

  // TU MANUSCRITO ES SIEMPRE LA ÚLTIMA VERSIÓN (Rais, 23-sep). Una pasada crea la v02 y el trabajo
  // sigue ahí: quedarse en la v01 hace que la siguiente pasada salga del texto viejo, y obligar al
  // autor a elegirla a mano es pedirle que haga de contable. Se cambia solo y se dice.
  useEffect(() => {
    if (!doc || !dir || !ficheros.length) return;
    const ultima = ultimaVersionDe(doc, ficheros);
    if (!ultima) return;
    señalar(`${dir}/${ultima}`);
    setAvisoArriba(`Tu manuscrito pasa a ser «${ultima}», la última versión de tu libro en la carpeta. La anterior sigue ahí, intacta.`);
  }, [doc, dir, ficheros]);

  // La propuesta que el autor ha decidido llevar al manuscrito. Se abre sobre el lienzo, arriba:
  // escribir en el libro no es una acción de la conversación, es una decisión sobre el libro.
  const [aplicando, setAplicando] = useState<{ texto: string; op?: string | null; base?: string } | null>(null);

  const elegirProyecto = (d: string, f: Fichero[], doc?: string) => {
    localStorage.setItem("hi.proyecto", d);
    // EL DOCUMENTO SEÑALADO, no solo su carpeta. Sin esto el panel adivinaba el manuscrito
    // cogiendo el fichero más grande, y en una carpeta como Descargas eso es siempre otro libro:
    // arriba ponía «Downloads» y en Analizar proponía un documento que el autor no eligió.
    if (doc) localStorage.setItem("hi.documento", doc);
    else localStorage.removeItem("hi.documento");
    setDoc(doc ?? null);
    setDir(d);
    setFicheros(f);
    irA("panel");
  };

  // Solo cuentan las cifras que existen de verdad. Las demás, ninguna.
  const guardado = dir ? localStorage.getItem(`hi.informe:${dir}`) : null;
  const cuentas: Record<string, string | number | undefined> = {
    manuscrito: ficheros.length || undefined,
    "informe-lectura": guardado ? (JSON.parse(guardado).chapterCount as number) : undefined,
  };

  const titulo = TITULOS[seccion] ?? "Proyectos";
  const encendida = (flujo ? PADRE_FLUJO : PADRE)[seccion] ?? seccion;

  if (!isLoaded) return <div className="puerta"><p className="vacio">Abriendo…</p></div>;
  if (!isSignedIn) return <Entrada />;

  // Hasta que el motor abra no se pinta nada del libro.
  if (!puerta) return <div className="puerta"><p className="vacio">Abriendo…</p></div>;
  if (puerta.modo !== "dentro") {
    return (
      <Puerta
        paso={puerta}
        alEntrar={() => setPuerta({ modo: "dentro", conPassword: true })}
        alMigrar={(pw) => { abrirPuerta(pw); }}
        alReintentar={() => { setPuerta(null); abrirPuerta(); }}
        alSalir={() => { salir().finally(() => clerk.signOut()); }}
      />
    );
  }

  return (
    <Flujo.Provider value={flujo}>
    <div className={"app" + (hablando ? " con-chat" : "") + (flujo ? " flujo" : "")} style={anchoChat.estilo}>
      <Lateral
        equipo={equipo} bandeja={bandejaConv} hablando={hablando} alHablar={hablar}
        gasto={gasto} version={est?.version ?? "…"} alIr={irA} seccion={encendida} actual={seccion} cuentas={cuentas} dir={dir}
        // Se vuelve a PREGUNTAR el estado en vez de darlo por hecho. Antes esto ponía
        // `cerrado: true` a pelo, así que quien no tenía contraseña puesta acababa delante de una
        // puerta que solo abría una contraseña inexistente: 401 en bucle hasta recargar la página.
        cerrado={puerta.conPassword} nombre={nombre}
        alSalir={() => { salir().then(() => abrirPuerta()).catch(() => {}); }}
      />
      <div className="col">
        <header className="top">
          {/* Lo primero que el autor necesita saber es EN QUÉ MANUSCRITO ESTÁ. Antes aquí salía
              solo el último tramo de la carpeta —«Downloads»—, que no dice nada de su libro. */}
          <div className="ruta">
            {doc
              ? <><b className="doc" title={doc}>{nombreDeRuta(doc)}</b><Pildora p={puntoDelLibro(doc, camino.hechos.plan)} /></>
              : <span>{dir ? nombreDeRuta(dir) : "Sin manuscrito"}</span>}
            {hablando && <><span className="sep">/</span><b className="quien-habla">{hablando === "reescritura" ? "Chat de reescritura" : flujo && hablando !== "coach" ? "Tu perfil de autor" : chat.colaborador?.name}</b></>}
          </div>
          <Barra etapas={camino.etapas} activa={seccion} alEtapa={irA} trabajando={etapaTrabajando} />
          <div className="acciones">
            {/* EL ESTADO DEL MOTOR. Hasta ahora el panel solo hablaba cuando fallaba, y lo hacía
                dentro del lienzo, mezclado con el trabajo. Pero «¿me está escuchando mi ordenador?»
                es la primera pregunta del autor cada vez que abre, y en un producto que vive de un
                servidor local merece respuesta permanente y de un vistazo. `est` ya existe: es lo
                que devuelve /health al arrancar. */}
            {/* La web se actualiza sola; el motor del ordenador no. Hasta ahora nadie se lo decía,
                así que un panel nuevo podía estar llamando a rutas que su motor no tiene. Va aquí y
                no en una tarjeta: es una condición del equipo, no una tarea del libro. */}
            {nueva && (
              <a className="pulso nuevo" href={nueva.url ?? "https://humanink.io/studio"}
                 target="_blank" rel="noopener"
                 title={`Tu motor es la ${est?.version}; hay una ${nueva.ultima}`}>
                <span className="punto" />Actualiza a la {nueva.ultima}
              </a>
            )}
            <span className={`pulso ${est ? "vivo" : "mudo"}`}>
              <span className="punto" />
              {est ? "Motor activo" : "Sin motor"}
            </span>
            {/* El chat se abre y se pliega a la derecha, con el agente de la etapa (R-12). */}
            <button className="btn fantasma" onClick={() => {
              if (hablando) { setHablando(null); return; }
              hablar(esEtapa(seccion) ? HABLAR_EN_ETAPA[seccion].agente : chat.elegido || "coach");
            }}>{flujo ? (hablando ? "Cerrar el coach" : "Coach") : hablando ? "Cerrar chat" : "Chat"}</button>
            <button className="btn texto" onClick={() => irA("ajustes")}>Ajustes</button>
          </div>
        </header>
        {/* LA CARPETA DEL LIBRO, SIEMPRE A LA VISTA (Rais, 2-oct-2026). Un probador: «¿dónde guarda
            lo que ha corregido? No lo encuentro». Todo lo que el Studio escribe cae aquí, así que la
            ruta va entera en todas las pantallas y se abre con un clic. No depende de la sección. */}
        {dir && (
          <div className="carpeta-libro">
            <span className="rotulo">Carpeta de tu libro</span>
            <span className="mono donde" title={dir}><bdi>{dir}</bdi></span>
            <button className="btn texto" onClick={async () => {
              // Se señala un fichero de dentro; si justo ese ya no está, la carpeta misma.
              const que = queEnsenar(dir, doc, ficheros);
              let r = await mostrarEnCarpetaCon(que);
              if (r === "no_esta" && que !== dir) r = await mostrarEnCarpetaCon(dir);
              const aviso = avisoDeCarpeta(r, dir);
              if (aviso) setAvisoArriba(aviso);
            }}>Abrir la carpeta</button>
          </div>
        )}
        {/* El progreso de lo que el motor está haciendo, bajo la barra, solo mientras dura. */}
        {trab.vivos.slice(0, 1).map((t) => (
          <div className="trabajo-vivo" key={t.id}>
            <div className={`barra-progreso${t.progreso?.total ? "" : " sin-saber"}`}>
              <i style={t.progreso?.total ? { width: `${Math.max(6, Math.round((t.progreso.hecho / t.progreso.total) * 100))}%` } : undefined} />
            </div>
            {/* El MISMO número que la tarjeta de la etapa: arriba decía «paso 0 de 8» y abajo «1 de 8». */}
            <p><span className="etiqueta-trabajando"><span className="punto" />Trabajando</span> {t.titulo}{t.progreso ? ` · ${t.progreso.fase ?? (t.progreso.total <= 1 ? "en marcha" : `${t.tipo === "informe-global" ? "capítulo" : "paso"} ${Math.min(t.progreso.hecho + 1, t.progreso.total)} de ${t.progreso.total}`)}` : " · empezando"} · puedes cerrar el Studio, sigue trabajando en tu ordenador</p>
          </div>
        ))}

        {/* CH-6: lo que ha terminado mientras hacías otra cosa, con un botón para verlo. */}
        <Terminados lista={trab.terminados} seccion={seccion} irA={irA} cerrar={trab.cerrar} />

        <main className="lienzo">
          <Salvavidas clave={seccion + "|" + (hablando ?? "")}>
          {fallo && <section className="carta"><p className="vacio">{fallo}</p></section>}
          {/* EL MANUSCRITO QUE YA NO ESTÁ (21-sep: tras renombrarlo, el Studio enseñaba
              «/file?path=… respondió 400»). Se dice con palabras y se ofrecen los documentos
              de la carpeta para elegir con un clic. */}
          {docDesaparecido && (
            <section className="carta">
              <h2>No encuentro tu manuscrito</h2>
              <p><b>«{nombreDeRuta(doc!)}»</b> ya no está en la carpeta del proyecto. Si lo has renombrado o
                movido, elige cuál es ahora:</p>
              <div className="botones">
                {ficheros.filter((f) => /\.docx$/i.test(f.name)).slice(0, 8).map((f) => (
                  <button key={f.path} className="btn fantasma" onClick={() => señalar(f.path)}>{f.name}</button>
                ))}
              </div>
            </section>
          )}
          {historial.length > 0 && (
            <button className="btn texto volver" onClick={volver} aria-label="Volver a la pantalla anterior">← Volver</button>
          )}
          {/* EL MANUSCRITO ELEGIDO VIVE FUERA DEL PROYECTO (Rais, 22-sep: el documento era el original
              de su disco externo). Se dice arriba, en todas las pantallas, y se puede traer de un
              clic: el motor lo COPIA dentro con su número de versión y no toca el original. */}
          {/* La carpeta guardada no puede ser un proyecto: se dice y se lleva a Proyectos (23-sep). */}
          {dir && carpetaImposible(dir, ficheros.length) && (
            <div className="aviso">
              <b>Esto no es la carpeta de un libro.</b> El Studio tiene abierta <b className="mono">{dir}</b>, y {carpetaImposible(dir, ficheros.length)}.
              Tus proyectos y su trabajo siguen donde estaban.
              <div className="botones"><button className="btn" onClick={() => irA("proyectos")}>Ir a Proyectos</button></div>
            </div>
          )}
          {doc && pendiente && ((pendiente.marcas ?? 0) > 0 || pendiente.abierto_en_word) && (
            <div className="aviso">
              <b>{nombreDeRuta(doc)}</b>{" "}
              {(pendiente.marcas ?? 0) > 0
                ? <>tiene <b>{pendiente.marcas} {pendiente.marcas === 1 ? "cambio" : "cambios"} sin decidir</b>. Si ya los decidiste en Word, falta <b>guardar</b>: el Studio solo ve lo que está guardado.</>
                : <>está decidido.</>}
              {pendiente.abierto_en_word && <> Word lo tiene abierto: guarda y ciérralo antes de la siguiente pasada.</>}
            </div>
          )}
          {/* Motor 1.7: los sitios donde te toca escribir a ti (no quieres texto de la IA y se te dejó dicho
              qué escribir). Un libro no se corrige ni se publica con uno dentro sin que lo sepas. */}
          {doc && avisoDeIndicaciones(pendiente?.indicaciones) && (
            <div className="aviso"><b>{nombreDeRuta(doc)}</b> {avisoDeIndicaciones(pendiente?.indicaciones)}</div>
          )}
          {dir && doc && !dentroDeCarpeta(doc, dir) && (
            <FueraDelProyecto dir={dir} doc={doc} alTraido={(f) => { setFicheros((v) => [...v, f]); señalar(f.path); latir(); }} alAviso={setAvisoArriba} />
          )}
          {avisoLlave && seccion !== "ajustes" && (
            <div className="aviso" style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span style={{ flex: 1 }}>{avisoLlave}</span>
              <button className="btn texto" onClick={() => irA("ajustes")}>Ir a Ajustes</button>
            </div>
          )}
          {avisoArriba && (
            <div className="aviso" style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span style={{ flex: 1 }}>{avisoArriba}</span>
              <button className="btn texto" onClick={() => setAvisoArriba(null)}>Entendido</button>
            </div>
          )}

          {(chat.sinSuscripcion || seccion === "suscripcion") && (
            <Suscribir
              alActivarse={() => { chat.setSinSuscripcion(false); setSeccion("panel"); }}
              alCerrar={chat.sinSuscripcion ? () => chat.setSinSuscripcion(false) : undefined}
            />
          )}

          {aplicando && abierto && (
            <Aplicar
              carpeta={dir} doc={abierto.ruta} original={aplicando.base ?? abierto.texto}
              propuesta={aplicando.texto} opId={aplicando.op ?? null}
              autor={chat.colaborador?.id === "copyeditor" ? "HumanInk · corrección y humanizador" : `${chat.colaborador?.name ?? "HumanInk"} · ${new Date().toISOString().slice(0, 10)}`}
              alCerrar={() => setAplicando(null)}
            />
          )}

          {!dir && seccion !== "ajustes" && <Proyecto dir={dir} alElegir={elegirProyecto} />}

          {/* LAS ETAPAS DEL CAMINO (R-10). Sin manuscrito, primero se pide (R-13); la etapa se enseña
              igual, porque recomienda y no bloquea. */}
          {esEtapa(seccion) && dir && (
            <>
              {!doc && <PrimeroElManuscrito carpeta={dir} ficheros={ficheros} alDoc={señalar} alSubido={subido} alAviso={setAvisoArriba} />}
              <Etapa id={seccion} etapa={camino.etapas.find((e) => e.id === seccion)!}
                     informe={camino.informe} plan={camino.plan} planEnCarpeta={camino.planEnCarpeta}
                     carpeta={dir} doc={doc} ficheros={ficheros} pulso={pulso} alHablar={hablarCon} alIr={irA}
                     trabajos={trab.lista} alLanzar={trab.refrescar} alCambio={latir} planSoportado={camino.planSoportado} alCambiarDoc={señalar}
                     alSeguirCon={(r) => { señalar(r); setAvisoArriba(`Tu manuscrito ahora es «${nombreDeRuta(r)}», la versión que acaba de crear la pasada. La anterior sigue en tu carpeta, intacta.`); latir(); }}
                     corrigiendo={chat.ocupado && chat.elegido === "copyeditor"}
                     alCorregir={flujo ? undefined : (capitulo) => {
                       hablar("copyeditor");
                       chat.enviar({ agente: "copyeditor", capitulo,
                         texto: "Corrige este texto: línea, estilo y ortotipografía, y pasa el humanizador (quita el IA-slop: muletillas de ensayo, paralelismos de plantilla, verbos comodín, moralejas; sin tocar la voz). Devuelve solo el texto corregido, completo, sin comentarios." });
                     }} />
            </>
          )}

          {seccion === "auditoria" && (
            <Fase
              titulo="Auditoría" items={HIJAS.auditoria} alIr={irA}
              pendiente={(id) => id in PORHACER}
              quehace={(id) => QUEHACE[id] ?? PORHACER[id]?.[0] ?? ""}
            />
          )}

          {(seccion === "proyectos" || seccion === "panel") && (
            <>
              {/* LOS PROYECTOS, primero: la pregunta «en qué libro estoy y cómo cambio» va antes
                  que cualquier cifra. La lista vive en el motor, no en este navegador, así que
                  sobrevive a vaciar los datos del sitio y el panel de Word puede leerla.
                  El panel de mando sigue debajo: la barra es de Rais y no le añado entradas. */}
              <Proyectos
                carpeta={dir}
                doc={doc}
                alDoc={(ruta) => {
                  localStorage.setItem("hi.documento", ruta);
                  setDoc(ruta);
                }}
                alElegir={(d) => {
                  if (d === dir) return;
                  localStorage.setItem("hi.proyecto", d);
                  // El documento es de otro libro: se suelta. Dejarlo señalando al manuscrito
                  // anterior es lo que hacía que Analizar propusiera un documento de otra carpeta.
                  localStorage.removeItem("hi.documento");
                  setDoc(null);
                  setFicheros([]);
                  setDir(d);
                }}
              />

              <div className="saludo">
                <h1>{saludoDelDia()}{(quienSaluda ?? nombre) ? `, ${quienSaluda ?? nombre}` : ""}.</h1>
                <p>
                  {doc
                    ? <>Trabajas sobre <b>{nombreDeRuta(doc)}</b>{camino.ultima ? <> · {miles(camino.ultima.palabras)} palabras en la última versión medida</> : null}.</>
                    : dir ? "Este proyecto todavía no tiene manuscrito." : "Elige o empieza un proyecto."}
                </p>
              </div>

              {dir && !doc && !perdido && <PrimeroElManuscrito carpeta={dir} ficheros={ficheros} alDoc={señalar} alSubido={subido} alAviso={setAvisoArriba} />}

              {/* El panel es un cuadro de mando, y con el manuscrito recién elegido todas sus cifras
                  son cero. Sin esta tarjeta el autor aterriza mirando ceros sin saber qué hacer:
                  pasó de verdad, y la frase fue «me dejó seleccionar un documento pero no sé más». */}
              {perdido && (
                <section className="carta">
                  <h2>No encuentro tu manuscrito<span className="sub">la carpeta ya no está</span></h2>
                  <p className="vacio">
                    Esta carpeta estaba elegida y ahora no existe en tu ordenador. Puede que la
                    hayas movido o renombrado, o que viviera en un disco que hoy no está conectado.
                    <br /><b className="dir">{perdido}</b>
                  </p>
                  <div className="dir">
                    <button
                      className="btn"
                      onClick={() => {
                        setPerdido(null);
                        setDir("");
                        setDoc(null);
                        setFicheros([]);
                        localStorage.removeItem("hi.proyecto");
                        localStorage.removeItem("hi.documento");
                      }}
                    >
                      Elegir otro manuscrito
                    </button>
                  </div>
                </section>
              )}

              {dir && !perdido && (!tele || tele.total.calls === 0) && (
                <section className="carta">
                  <h2>Tu primer encargo<span className="sub">por dónde se empieza</span></h2>
                  <p className="vacio">
                    Ya tienes el manuscrito puesto. Las cifras de aquí abajo están a cero porque
                    todavía no le has pedido nada a nadie: se llenan solas con el primer encargo.
                  </p>
                  <ol className="pasos">
                    {!est?.hasKey && (
                      <li>
                        <b>Pon tu llave de OpenRouter</b> en Ajustes. El trabajo lo pagas tú a tu
                        proveedor, y tu texto no pasa por nosotros.
                      </li>
                    )}
                    <li>
                      <b>Empieza por Diagnóstico</b>, arriba: el camino de tu libro va de izquierda a
                      derecha y cada etapa te dice qué conviene hacer.
                    </li>
                    <li>
                      <b>Pídeselo con tus palabras.</b> Lo que produzca cae en la carpeta de tu
                      manuscrito, junto al texto al que pertenece.
                    </li>
                  </ol>
                  {!est?.hasKey && (
                    <div className="dir">
                      <button className="btn" onClick={() => irA("ajustes")}>Poner mi llave</button>
                    </div>
                  )}
                </section>
              )}

              {/* EL PANEL ES EL ESTADO DEL LIBRO (Fase 12, R-11): lo primero, dónde está tu libro;
                  lo que llevas gastado, plegado al final. */}
              {dir && (
                <div className="rejilla">
                  {camino.etapas.slice(0, 2).map((e) => (
                    <button className="carta tarjeta-etapa" key={e.id} onClick={() => irA(e.id)}>
                      <h2>{e.nombre}<span className="sub">{e.estado === "hecha" ? "hecha" : e.estado === "en_curso" ? "en curso" : e.estado === "sin_datos" ? "sin datos" : "sin empezar"}</span></h2>
                      <div className="cifra"><b>{e.id === "diagnostico" ? (camino.hechos.informe?.nota != null ? String(camino.hechos.informe.nota).replace(".", ",") : "—") : camino.hechos.plan?.total ? `${camino.hechos.plan.hechas}/${camino.hechos.plan.total}` : "—"}</b>
                        <span>{e.id === "diagnostico" ? "nota del informe de lectura" : "pasadas"}</span></div>
                      <p className="vacio">{e.recomendado ? `Recomendado antes: ${e.recomendado}` : e.detalle}</p>
                    </button>
                  ))}
                  <button className="carta tarjeta-etapa" onClick={() => irA("versiones")}>
                    <h2>Versión<span className="sub">{camino.versiones.length ? `${camino.versiones.length} medidas` : "sin medir"}</span></h2>
                    {/* El nombre del build es texto, no una cifra: con `nombre` se ajusta a la tarjeta
                        en vez de salirse y quedar cortado (Rais, 23-sep). */}
                    <div className="cifra"><b className="nombre">{camino.ultima?.build ? nombreDeRuta(camino.ultima.build).replace(/\.docx$/i, "") : "—"}</b>
                      <span>{camino.ultima ? `${miles(camino.ultima!.palabras)} palabras` : ""}</span></div>
                    <p className="vacio">{camino.ultima?.delta != null ? `${camino.ultima!.delta! > 0 ? "+" : ""}${miles(camino.ultima!.delta!)} respecto a la anterior` : "La curva se llena con cada pasada."}</p>
                  </button>
                  <SinDecidir dir={dir} doc={doc} resumen={resumen} alLeido={latir}
                              alVerCuales={() => { irA("bitacora"); setSoloPendientes(true); }} />
                </div>
              )}

              {dir && doc && (() => {
                const toca = camino.etapas.find((e) => e.estado !== "hecha" && e.estado !== "sin_datos");
                return toca ? (
                  <section className="carta">
                    <h2>Siguiente paso<span className="sub">recomendado</span></h2>
                    <div className="botones" style={{ justifyContent: "space-between" }}>
                      <div><b>{toca.nombre}</b><p className="vacio">{toca.detalle}</p></div>
                      <button className="btn" onClick={() => irA(toca.id)}>Ir a {toca.nombre}</button>
                    </div>
                  </section>
                ) : null;
              })()}

              <details className="carta plegable">
                <summary><h2>Lo que llevas gastado<span className="sub">{tele ? dolares(tele.total.cost) : "0,00 $"} · {tele ? miles(tele.total.calls) : 0} encargos</span></h2></summary>
                <div className="rejilla">
                  <section className="carta">
                    <h2>Qué te cuesta cada colaborador<span className="sub">acumulado</span></h2>
                    <div className="cifra"><b>{tele ? dolares(tele.total.cost) : "0,00 $"}</b><span>de tu propia llave</span></div>
                    {tele && <GastoGrafico t={tele} nombres={nombres} />}
                  </section>
                  <section className="carta">
                    <h2>Trabajo hecho<span className="sub">desde que instalaste</span></h2>
                    <div className="cifra"><b>{tele ? miles(tele.total.calls) : "0"}</b><span>encargos</span></div>
                    <p className="vacio">{tele ? `${miles(tele.total.tokens)} tokens · ${Math.round(tele.total.ms / 60000)} minutos de trabajo` : "Se llena con el primer encargo."}</p>
                  </section>
                  <section className="carta">
                    <h2>Tu manuscrito<span className="sub">{dir ? "en tu disco" : "sin elegir"}</span></h2>
                    <div className="cifra"><b>{dir ? ficheros.length : "0"}</b><span>documentos</span></div>
                    <p className="vacio">{dir || "Elige la carpeta para que los entregables no acaben sueltos."}</p>
                  </section>
                </div>
              </details>
            </>
          )}

          {seccion === "manuscrito" && (
            ficheros.length
              ? (
                <>
                  <Manuscrito ficheros={ficheros} abierto={abierto} alAbrir={setAbierto} doc={doc} alElegir={señalar} />
                  {/* Y subir otro o traerlo de fuera, también con un manuscrito ya elegido. */}
                  {doc && <PrimeroElManuscrito carpeta={dir} ficheros={ficheros} alDoc={señalar} alSubido={subido} alAviso={setAvisoArriba} cambiar />}
                  {/* Los dos documentos que acompañan al manuscrito. El motor los busca por su
                      nombre exacto y dice a qué colaborador pedir lo que falta (Manuscrito I,
                      13-sep-2026): sin manuscrito, biblia+escaleta+estilo con el coach, o al
                      escritor fantasma si ya están; con manuscrito y sin alguna, al coach para
                      documentarlo. Un motor anterior a esta ruta no rompe nada: sin sugerencia,
                      queda el botón de siempre. */}
                  {!flujo && <div className="rejilla">
                    {(["biblia", "escaleta"] as const).map((d) => {
                      const dato = estadoMs?.[d];
                      const hay = dato?.existe;
                      const nombre = dato?.ruta?.split(/[/\\]/).pop();
                      return (
                        <section className="carta ficha" key={d}>
                          <h2>{TITULOS[d]}<span className="sub">{estadoMs == null ? "…" : hay ? "en la carpeta" : "todavía no"}</span></h2>
                          <p className="vacio">{hay ? nombre : PORHACER[d][0]}</p>
                          {hay
                            ? <button className="btn fantasma" onClick={() => setAbierto(null)} disabled>Se abre desde la lista</button>
                            : (
                              <div className="botones">
                                <button className="btn" onClick={() => { hablar("coach"); chat.setEncargo(`Quiero hacer la ${d} de mi libro. Guíame.`); }}>Pedírsela al coach</button>
                                {dir && <SubirDocumento carpeta={dir} tipo={d} alSubido={(f, aviso) => { subido(f); setAvisoArriba(aviso); latir(); }} />}
                              </div>
                            )}
                        </section>
                      );
                    })}
                  </div>}
                  {!flujo && estadoMs?.sugerencia && (
                    <section className="carta">
                      <h2>Por dónde seguir<span className="sub">según lo que ya tienes</span></h2>
                      <p className="vacio">{estadoMs.sugerencia.texto}</p>
                      <div className="dir">
                        <button className="btn" onClick={() => {
                          const col = estadoMs.sugerencia!.colaborador;
                          hablar(col);
                          chat.setEncargo(col === "ghostwriter"
                            ? "Ya tengo la biblia y la escaleta. Vamos a escribir el manuscrito."
                            : "Quiero desarrollar mi libro antes de escribir: biblia, escaleta y estilo. Guíame.");
                        }}>
                          {estadoMs.sugerencia.colaborador === "ghostwriter" ? "Ir al escritor fantasma" : "Ir al coach"}
                        </button>
                      </div>
                    </section>
                  )}
                </>
              )
              : <Proyecto dir={dir} alElegir={elegirProyecto} />
          )}

          {seccion === "modelos" && <Modelos />}
          {/* El perfil lo conduce el colaborador de perfil (`author`), que ya tiene la entrevista de
              dos rondas. Cuando exista el coach compuesto (Agentes I) lo conducirá él. */}
          {seccion === "autores" && (
            <Autores carpeta={dir} alPerfil={(a) => {
              hablar("author");
              chat.setEncargo(`Quiero hacer el perfil de autor de la firma «${a.nombre}»${a.seudonimos.length ? ` (también firma como ${a.seudonimos.join(", ")})` : ""}. Hazme las preguntas por rondas. Cuando acabemos, lo guardo con «Escribir el perfil con la entrevista», en Autores.`);
            }} />
          )}

          {seccion === "estilos" && <Estilos carpeta={dir} doc={doc} alHablar={hablarCon} />}

          {seccion === "informe-lectura" && <Informe ficheros={ficheros} carpeta={dir} doc={doc} pulso={pulso} trabajo={trab.ultimo("informe-global")} alLanzar={trab.refrescar}
                                                     plan={camino.plan} alCambioPlan={latir} alIr={irA} />}

          {seccion === "bitacora" && (
            <Bitacora carpeta={dir} doc={doc} soloPendientes={soloPendientes}
                      alSoltarPendientes={() => setSoloPendientes(false)} alDecidir={latir} />
          )}
          {/* Registro: la misma bitácora, abierta en lo que AWAP sella y firma. */}
          {seccion === "registro" && <Bitacora carpeta={dir} inicial="awap" />}
          {seccion === "certificado" && <Certificado carpeta={dir} doc={doc} pulso={pulso} />}
          {seccion === "procedencia" && <Procedencia doc={doc} />}

          {(seccion === "versiones" || seccion === "comparar") && <Pasadas carpeta={dir} doc={doc} pulso={pulso} trabajo={trab.ultimo("medir")} alLanzar={trab.refrescar}
                                                                   alCambiarDoc={señalar} alCambio={latir} />}

          {/* CONTROL (3-oct-2026): el cuadro de mandos del libro. Las cifras del texto y cómo cambian
              de una versión a otra; el gasto, al final. */}
          {seccion === "control" && (
            <Control carpeta={dir} doc={doc} pulso={pulso} trabajo={trab.ultimo("medir")} alLanzar={trab.refrescar}
                     alCambiarDoc={señalar} alCambio={latir} alIr={irA} nombres={nombres} />
          )}

          {seccion === "gasto" && (dir
            ? <Gastos carpeta={dir} nombres={nombres} />
            : <section className="carta"><h2>Gasto</h2><p className="vacio">Elige antes un proyecto: el gasto se cuenta por libro.</p></section>)}
          {seccion === "ayuda" && <Ayuda version={est?.version} />}

          {seccion === "ajustes" && (
            // 17-sep (Rais): «en esta pantalla el manuscrito no pinta nada». Qué manuscrito, qué
            // proyecto y qué autor se ven y se cambian en Proyectos, no en Ajustes.
            <Ajustes est={est} />
          )}

          {PORHACER[seccion] && (
            <Pendiente titulo={titulo} hara={PORHACER[seccion][0]} falta={PORHACER[seccion][1]} />
          )}

          </Salvavidas>
        </main>
      </div>

      {/* EL CHAT, A LA DERECHA (Fase 12, R-12). Se habla con el agente sin perder de vista el trabajo
          de la etapa; se pliega con «Cerrar chat». En pantallas estrechas ocupa la pantalla entera.
          El compositor solo existe cuando hay alguien enfrente. */}
      {hablando && (
        <aside className="chat-lado">
          <BordeChat ancho={anchoChat.ancho} setAncho={anchoChat.setAncho} />
          <div className="chat-cierre">
            <button className="btn texto" onClick={() => setHablando(null)} aria-label="Cerrar el chat">✕ Cerrar</button>
          </div>
          <div className="chat-cuerpo">
            <Salvavidas clave={"chat|" + hablando}>
              {hablando === "reescritura"
                ? <Reescritura carpeta={dir} doc={doc} alGastar={refrescarGasto} alSinSuscripcion={() => chat.setSinSuscripcion(true)} alHablar={hablarCon} />
                : <Conversacion c={chat} alAplicar={setAplicando} />}
            </Salvavidas>
          </div>
          {hablando !== "reescritura" && (
            <Compositor c={chat} ficheros={ficheros} carpeta={dir} doc={doc} tele={tele} alSubir={subido} />
          )}
        </aside>
      )}
    </div>
    </Flujo.Provider>
  );
}

/**
 * El manuscrito está fuera de la carpeta del proyecto: lo que se lee y lo que se escribe acabarían
 * en sitios distintos. Se ofrece traerlo (copiar) al proyecto, que es lo único que no rompe nada.
 */
function FueraDelProyecto({ dir, doc, alTraido, alAviso }: {
  dir: string; doc: string; alTraido: (f: Fichero) => void; alAviso: (t: string) => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  return (
    <div className="aviso">
      <b>Tu manuscrito está fuera del proyecto.</b> Has elegido <b className="mono">{doc}</b>, y el proyecto es{" "}
      <b className="mono">{dir}</b>. Se lee de un sitio y se escribiría en otro, así que trabaja sobre una copia dentro
      del proyecto: tu original no se toca nunca.
      <div className="botones">
        <button className="btn" disabled={ocupado} onClick={async () => {
          setOcupado(true); setFallo(null);
          try {
            const r = await traerAlProyecto(dir, doc);
            alTraido({ path: r.ruta, name: r.nombre, ext: ".docx", size: 0 });
            alAviso(`Tu manuscrito está ahora en el proyecto como «${r.nombre}»${r.marcas ? `, con ${r.marcas} cambios sin decidir que tienes que resolver en Word` : ""}. El original no se ha tocado.`);
          } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
        }}>{ocupado ? "Copiándolo…" : "Traerlo al proyecto"}</button>
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </div>
  );
}

/** La píldora de dónde está el libro: manuscrito · versión · pasada. Lo que no se sabe, no sale. */
// Rais, 21-sep: «tiene que salir el nombre del fichero completo arriba». El manuscrito y la versión
// ya están en el nombre (DMVM-m01-v01.docx); la píldora solo añade lo que el nombre no dice: sin
// número de versión, que no lo tiene, y en qué pasada del plan va.
function Pildora({ p }: { p: PuntoDelLibro }) {
  const partes = [p.version ? null : "sin número de versión", p.pasada].filter(Boolean);
  if (!partes.length) return null;
  return <span className="pildora" title="Versión y pasada del plan">{partes.join(" · ")}</span>;
}
